// 商户后台顶部栏：搜索 + 通知 + 商户信息
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@/components/Toast';
import { Bell, Search, ChevronDown, Store } from 'lucide-react';
import { loadSession } from '@/api/client';
import { logout } from '@/api/auth';
// 需求9 / P-003：消息组不再作为一级菜单，铃铛为唯一入口（链 /merchant/messages）；
// 角标统一走 useMerchantBadges 的消息未读数（与侧栏同源同事件刷新，禁写死数字）
import { useMerchantBadges } from '@/hooks/useMerchantBadges';

// 搜索关键字 → 路由映射
const SEARCH_MAP: { keywords: string[]; path: string; label: string }[] = [
  { keywords: ['充值', 'topup', '通道充值'], path: '/merchant/channel/topup', label: '通道充值' },
  { keywords: ['费率', 'rates', '交易费率'], path: '/merchant/channel/rates', label: '交易费率' },
  { keywords: ['用户', '持卡人', 'user'], path: '/merchant/users', label: '用户管理' },
  { keywords: ['kyc', '认证'], path: '/merchant/kyc', label: 'KYC 管理' },
  { keywords: ['代填', 'kyc代填', 'kyc fill', '填kyc'], path: '/merchant/kyc/fill', label: 'KYC 代填' },
  { keywords: ['罚款', '欠费', '巡检罚款'], path: '/merchant/kyc/fines', label: '罚款记录' },
  { keywords: ['虚拟卡', '实体卡', '开卡', 'virtual', 'physical'], path: '/merchant/card/issue-center', label: '新卡管理' },
  { keywords: ['充值配置', 'recharge'], path: '/merchant/business/recharge-config', label: '卡片充值配置' },
  { keywords: ['用户充值', 'deposit'], path: '/merchant/business/deposits', label: '用户充值' },
  { keywords: ['资金', 'finance', '钱包'], path: '/merchant/finance/channel', label: '通道资金' },
  { keywords: ['佣金', '结算', 'commission', 'settlement'], path: '/merchant/finance/commissions', label: '佣金管理' },
  { keywords: ['收支', 'pnl', '实时收支'], path: '/merchant/finance/pnl', label: '实时收支看板' },
  { keywords: ['品牌', 'brand'], path: '/merchant/brand', label: '品牌装修中心' },
  { keywords: ['设置', '安全', 'setting'], path: '/merchant/settings/account', label: '账户资质信息' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const toast = useToast();
  const { pathname } = useLocation();
  // 徽章动态计数（useMerchantBadges 统一数据源）：接口失败 / 尚未加载 → null（隐藏，不显示错误数字）；0 → 隐藏徽章
  const { unreadNoticeCount } = useMerchantBadges(pathname);
  const showNoticeBadge = unreadNoticeCount !== null && unreadNoticeCount > 0;

  // 用户信息：优先读会话 principal，硬编码兜底（未登录 / mock 降级展示）
  const session = loadSession();
  const principal = session?.me?.principal || session?.principal;
  const displayName = principal || '富盛金融科技';
  const displayEmail = principal || 'admin@fusheng.hk';

  const handleLogout = async () => {
    await logout();
    toast.success('已退出登录');
  };

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !keyword.trim()) return;
    const kw = keyword.trim().toLowerCase();
    const match = SEARCH_MAP.find((s) => s.keywords.some((k) => kw.includes(k.toLowerCase()) || k.toLowerCase().includes(kw)));
    if (match) {
      navigate(match.path);
      toast.info(`已跳转到「${match.label}」`);
      setKeyword('');
    } else {
      toast.error('未找到匹配的页面，请尝试：充值 / 用户 / KYC / 虚拟卡 等');
    }
  };

  return (
    <header className="h-14 flex-shrink-0 bg-white border-b border-slate-200 flex items-center justify-between px-6">
      <div className="flex-1" />
      <div className="flex items-center gap-4">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={handleSearch}
            className="w-64 pl-9 pr-3 py-1.5 rounded-lg bg-slate-100 border border-transparent text-sm placeholder:text-slate-400 focus:outline-none focus:bg-white focus:border-slate-300 transition"
            placeholder="搜索页面（回车跳转）..."
          />
        </div>
        {/* 铃铛：点击进入消息中心（替换原 toast.info('暂无新通知') 占位）；徽章为动态未读数 */}
        <button
          onClick={() => navigate('/merchant/messages')}
          className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          title="消息中心"
          aria-label={showNoticeBadge ? `消息中心，${unreadNoticeCount} 条未读` : '消息中心'}
        >
          <Bell className="w-4.5 h-4.5" />
          {/* 徽章 = unreadCount 动态获取（AC-08/AC-NTF-002）：0 或接口失败隐藏，禁写死数字 */}
          {showNoticeBadge && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-red-500 rounded-full text-[10px] text-white font-medium flex items-center justify-center">
              {unreadNoticeCount > 99 ? '99+' : unreadNoticeCount}
            </span>
          )}
        </button>
        <div className="relative">
          <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-100 transition-colors">
            <div className="w-7 h-7 rounded-full bg-brand-600 text-white flex items-center justify-center"><Store className="w-3.5 h-3.5" /></div>
            <div className="text-left">
              <p className="text-sm font-medium text-slate-800 leading-tight">{displayName}</p>
              <p className="text-[11px] text-slate-400 leading-tight">MCH-DEMO · V2</p>
            </div>
            <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {open && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-48 card py-1 z-20">
                <div className="px-3 py-2 border-b border-slate-100">
                  <p className="text-sm font-medium text-slate-700">{displayName}</p>
                  <p className="text-xs text-slate-400">{displayEmail}</p>
                </div>
                <button onClick={() => { setOpen(false); navigate('/merchant/settings/account'); toast.info('已打开账户资质'); }} className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">账户资质</button>
                <button onClick={() => { setOpen(false); navigate('/merchant/settings/security'); toast.info('已打开密码与安全'); }} className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">密码与安全</button>
                <button onClick={() => { setOpen(false); void handleLogout(); }} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50">退出登录</button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
